import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import { SHOW_PAYMENT } from "@/lib/paymentVisibility";
import OfflineStorageCard from "@/components/OfflineStorageCard";
import { Heart, Radio, CreditCard, Plus, Sprout, Lightbulb, ArrowLeft } from "lucide-react";

/** A child's Noor Garden — one leaf per completed story, so progress is a
 *  picture a parent reads in a second rather than a number they decode. */
function NoorGarden({ completed }) {
  const leaves = Array.from({ length: 12 }, (_, i) => i < Math.min(completed, 12));
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="noor-garden">
      {leaves.map((grown, i) => (
        <div key={i}
             className={`w-6 h-6 rounded-full flex items-center justify-center ${
               grown ? "bg-[#E3F1EC]" : "bg-[#F0EBD8]"}`}>
          <Sprout size={13} strokeWidth={2.5} className={grown ? "text-hv-teal" : "text-hv-slate/30"} />
        </div>
      ))}
    </div>
  );
}

export default function FamilyDashboard() {
  const [children, setChildren] = useState(null);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { data } = await client.get("/family/children");
    setChildren(data.children);
  }, []);

  useEffect(() => { load(); }, [load]);

  const link = async () => {
    if (!email.trim()) return;
    setMsg("");
    try {
      const value = email.trim();
      // A 6-character code (no @) links the child directly; anything else is their email.
      const isCode = /^[A-Za-z0-9]{6}$/.test(value);
      await client.post("/family/link", isCode ? { link_code: value } : { child_email: value });
      setEmail("");
      load();
    } catch (ex) {
      setMsg(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="family-dashboard">
      <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-1.5 text-hv-teal font-bold text-sm mb-5">
        <ArrowLeft size={16} strokeWidth={2.5} /> Back to dashboard
      </Link>
      <div className="mb-8">
        <OfflineStorageCard />
      </div>
      <span className="hv-badge">Your family</span>
      <h1 className="hv-heading mt-4 text-3xl sm:text-4xl">
        <span className="text-hv-navy">How everyone is</span> <span className="text-hv-coral">getting on.</span>
      </h1>

      <div className="mt-8 grid md:grid-cols-2 gap-5">
        {(children || []).map((c) => (
          <div key={c.id} className="hv-card p-7" data-testid={`child-card-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-hv-teal text-white flex items-center justify-center font-bold text-lg">
                  {(c.name || c.email).slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div className="hv-heading text-hv-navy text-lg">{c.name || c.email}</div>
                  <div className="text-hv-slate text-xs">{c.completed} of {c.unlocked} stories done</div>
                </div>
              </div>
              <div className="hv-heading text-3xl text-hv-teal">{c.completion_pct}%</div>
            </div>

            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-hv-teal font-bold">Noor Garden</div>
              <div className="mt-2"><NoorGarden completed={c.completed} /></div>
            </div>

            {c.days_to_live !== null && c.days_to_live !== undefined && (
              <div className="mt-5 flex items-center gap-2 text-sm text-hv-navy" data-testid={`child-live-${c.id}`}>
                <Radio size={16} className="text-hv-coral" strokeWidth={2.5} />
                <span className="font-bold">
                  {c.days_to_live === 0 ? "Live session is today" : `Live session in ${c.days_to_live} days`}
                </span>
              </div>
            )}
          </div>
        ))}

        {children && !children.length && (
          <div className="hv-card p-7 text-hv-slate" data-testid="no-children">
            No children linked yet. Add one with the email they sign in with.
          </div>
        )}
        {!children && <div className="p-6 text-hv-slate">Loading…</div>}
      </div>

      {/* Link a child */}
      <div className="mt-6 hv-card p-7" data-testid="link-child">
        <div className="flex items-center gap-3">
          <div className="hv-icon-circle-coral w-11 h-11 rounded-full flex items-center justify-center">
            <Heart size={20} strokeWidth={2.5} />
          </div>
          <span className="hv-badge hv-badge-coral">Add a child</span>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                 placeholder="Their sign-in email or 6-character code" data-testid="child-email"
                 className="flex-1 min-w-[240px] rounded-2xl border-2 border-[#E5DFC8] px-4 py-3 min-h-[44px] bg-white" />
          <button onClick={link} data-testid="link-child-submit" className="hv-btn-primary inline-flex items-center gap-2">
            <Plus size={18} strokeWidth={2.5} /> Link child
          </button>
        </div>
        {msg && <div className="mt-3 text-sm font-bold text-hv-coral" data-testid="link-error">{msg}</div>}
      </div>

      {/* Billing + community, one tap away — not a second app to learn */}
      <div className={`mt-6 grid gap-5 ${SHOW_PAYMENT ? "md:grid-cols-2" : ""}`}>
        {SHOW_PAYMENT && <div className="hv-card p-7" data-testid="billing-card">
          <div className="hv-icon-circle w-11 h-11 rounded-full flex items-center justify-center">
            <CreditCard size={20} strokeWidth={2.5} />
          </div>
          <h2 className="hv-heading mt-4 text-lg text-hv-navy">Billing</h2>
          <p className="mt-2 text-hv-slate text-sm">$10 / month · cancel anytime.</p>
          <p className="mt-3 text-hv-slate text-xs">Payments are not wired up yet — no card is on file.</p>
        </div>}
        <Link to="/community" className="hv-card p-7 block transition hover:-translate-y-1 hover:shadow-lg" data-testid="community-card">
          <div className="hv-icon-circle-gold w-11 h-11 rounded-full flex items-center justify-center">
            <Lightbulb size={20} strokeWidth={2.5} />
          </div>
          <h2 className="hv-heading mt-4 text-lg text-hv-navy">Story Idea Box</h2>
          <p className="mt-2 text-hv-slate text-sm">Suggest a story your family would love, and vote on others.</p>
        </Link>
      </div>
    </main>
  );
}
